import { defaultCourseId, k01Artifacts, k01Cases, k01Scenes, type CourseId } from "../app/k01/k01-content";

const STORAGE_KEY = "framlage-courses:v3";
const LEGACY_STORAGE_KEY = "framlage-k01:v2";
const OLDEST_STORAGE_KEY = "framlage-k01:v1";
const CONTENT_VERSION = "K01-1.1-teen";
const availableCourseIds = new Set<CourseId>(["general", "el-etg"]);
const driverIds = ["datorkraft", "datatillgång", "investeringar", "automatiseringsbehov", "arbetsmiljö", "säkerhet", "efterfrågan"];
const enumValues = new Map<string, Set<string>>([
  ["path", new Set(["human", "rule", "model"])],
  ["pathEvidence", new Set(["steps", "errors", "responsibility"])],
  ["rule", new Set(["tag", "symbol", "length"])],
  ["interface", new Set(["chat", "robot", "smart", "model"])],
  ["surfaceGuess", new Set(["ai", "fixed-rule", "unknown"])],
  ["surfaceEvidence", new Set(["trained-model-visible", "fixed-rule-visible", "path-hidden"])],
  ["dataset", new Set(["A", "B"])],
  ["prediction", new Set(["teori", "övning", "osäkert"])],
  ["errorCause", new Set(["data", "representation", "model", "goal"])],
  ["gate", new Set(["before", "during", "after"])],
  ["driver", new Set(driverIds)],
  ["demand", new Set(["study", "marketing"])],
  ["moment", new Set(["repeat", "overview", "risk"])],
  ["support", new Set(["automation", "ai", "stop"])],
  ["zone", new Set(["explore", "support", "stop"])],
  ["counterDriver", new Set(driverIds)],
  ["artifact:transferTrack", new Set(["school", "el-etg", "own-idea"])],
  ["artifact:systemCaseId", new Set(Object.values(k01Cases.transferCases).flat().map((item) => item.id))],
]);
const mechanismValues = new Set(["ai", "ordinary-code", "automation", "human", "unknown"]);
const winnerValues = new Set(["human", "ai", "team"]);
const evidenceValues = new Set([...k01Cases.systemCases.flatMap((item) => item.shownEvidence), "bara namnet och utseendet"]);
enumValues.set("artifact:teenChoice", new Set(k01Artifacts.flatMap((artifact) => artifact.teenCore.choiceOptions.map((option) => option.id))));
for (const artifact of k01Artifacts) {
  for (const field of artifact.fields) {
    if (!field.options?.length) continue;
    if (field.type === "driver-checklist") {
      for (const option of field.options) enumValues.set(`artifact:${field.id}:${option}`, new Set(["supports", "does-not-support"]));
    } else if (field.type === "enum-plus-reason") {
      enumValues.set(`artifact:${field.id}:choice`, new Set(field.options));
    } else if (field.type === "lens-question") {
      enumValues.set(`artifact:${field.id}:lens`, new Set(field.options));
    } else {
      enumValues.set(`artifact:${field.id}`, new Set(field.options));
    }
  }
}
const validToggleKeys = new Set([
  "path-seen:human", "path-seen:rule", "path-seen:model",
  ...k01Cases.trainingCards.map((card) => `train:${card.id}`),
  "feature:title", "feature:symbol", "feature:length", "feature:label",
  "question:0", "question:1", "question:2",
  "signal:title", "signal:symbol", "signal:length",
  "time:touched",
  ...["snabb dataanalys", "repetition", "sammanhang", "intuition", "social interaktion", "etik", "ansvar"].map((item) => `criterion:${item}`),
  ...driverIds.map((driver) => `artifact:sevenDrivers:${driver}`),
]);

export interface K01SceneProgress {
  values: Record<string, string>;
  numbers: Record<string, number>;
  toggles: string[];
  orders: Record<string, string[]>;
  runs: number;
  revealed: boolean;
  saved: boolean;
}

export interface K01Progress {
  schemaVersion: 3;
  contentVersion: "K01-1.1-teen";
  courseId: CourseId;
  currentSceneId: string;
  completedSceneIds: string[];
  listenedSceneIds: string[];
  sceneProgress: Record<string, K01SceneProgress>;
  artifacts: Record<string, Record<string, string>>;
}

export interface FramlageProgress {
  schemaVersion: 3;
  contentVersion: "K01-1.1-teen";
  activeView: "library" | "course" | "scene" | "data";
  activeCourseId: CourseId | null;
  startedCourseIds: CourseId[];
  courses: Record<CourseId, K01Progress>;
}

export function emptyK01SceneProgress(): K01SceneProgress {
  return { values: {}, numbers: {}, toggles: [], orders: {}, runs: 0, revealed: false, saved: false };
}

export function emptyK01Progress(courseId: CourseId = defaultCourseId): K01Progress {
  return {
    schemaVersion: 3,
    contentVersion: CONTENT_VERSION,
    courseId,
    currentSceneId: k01Scenes[0]?.id ?? "K01-S01-C01",
    completedSceneIds: [],
    listenedSceneIds: [],
    sceneProgress: {},
    artifacts: {},
  };
}

export function emptyFramlageProgress(): FramlageProgress {
  return {
    schemaVersion: 3,
    contentVersion: CONTENT_VERSION,
    activeView: "library",
    activeCourseId: null,
    startedCourseIds: [],
    courses: {
      general: emptyK01Progress("general"),
      "el-etg": emptyK01Progress("el-etg"),
    },
  };
}

const cleanStringRecord = (value: unknown, maxValueLength = 1200): Record<string, string> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value)
      .filter((entry): entry is [string, string] => typeof entry[1] === "string")
      .slice(0, 80)
      .map(([key, item]) => [key.slice(0, 100), item.slice(0, maxValueLength)]),
  );
};

const cleanSceneValues = (value: unknown): Record<string, string> => {
  const record = cleanStringRecord(value);
  return Object.fromEntries(Object.entries(record).filter(([key, item]) => {
    const allowed = enumValues.get(key)
      ?? (key.startsWith("class:") ? mechanismValues : undefined)
      ?? (key.startsWith("reason:") ? evidenceValues : undefined)
      ?? (key.startsWith("winner:") ? winnerValues : undefined);
    return !allowed || allowed.has(item);
  }));
};

const cleanNumberRecord = (value: unknown): Record<string, number> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const limits = (key: string): [number, number] | undefined => {
    if (key === "caseIndex") return [0, 5];
    if (key === "phase" || key === "driverPage" || ["budgetA", "budgetB", "budgetC"].includes(key)) return [0, 3];
    if (key === "artifactPage") return [0, 7];
    if (key === "artifactDriverPage") return [0, 6];
    if (key === "artifactAdvanced") return [0, 1];
    if (["time", "compute", "data", "counterValue"].includes(key) || key.startsWith("driver:")) return [0, 2];
    return undefined;
  };
  return Object.fromEntries(
    Object.entries(value)
      .filter((entry): entry is [string, number] => typeof entry[1] === "number" && Number.isFinite(entry[1]))
      .slice(0, 80)
      .flatMap(([key, item]) => {
        const range = limits(key);
        return range ? [[key, Math.max(range[0], Math.min(range[1], item))] as [string, number]] : [];
      }),
  );
};

const cleanStringArray = (value: unknown, max = 80): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === "string").slice(0, max).map((item) => item.slice(0, 100)) : [];

function cleanSceneProgress(value: unknown): K01SceneProgress {
  if (!value || typeof value !== "object" || Array.isArray(value)) return emptyK01SceneProgress();
  const source = value as Partial<K01SceneProgress>;
  const orders: Record<string, string[]> = {};
  if (source.orders && typeof source.orders === "object" && !Array.isArray(source.orders)) {
    for (const [key, order] of Object.entries(source.orders).slice(0, 12)) {
      if (key !== "chain") continue;
      const cleaned = cleanStringArray(order, 20);
      const allowed = ["data", "training", "model", "inference", "result", "human"];
      if (cleaned.length === allowed.length && new Set(cleaned).size === allowed.length && cleaned.every((item) => allowed.includes(item))) orders.chain = cleaned;
    }
  }
  return {
    values: cleanSceneValues(source.values),
    numbers: cleanNumberRecord(source.numbers),
    toggles: cleanStringArray(source.toggles).filter((key) => validToggleKeys.has(key)),
    orders,
    runs: typeof source.runs === "number" && Number.isFinite(source.runs) ? Math.max(0, Math.min(100, Math.trunc(source.runs))) : 0,
    revealed: source.revealed === true,
    saved: source.saved === true,
  };
}

function cleanCourseProgress(value: unknown, courseId: CourseId): K01Progress {
  if (!value || typeof value !== "object" || Array.isArray(value)) return emptyK01Progress(courseId);
  const source = value as Partial<K01Progress>;
  if (source.contentVersion !== CONTENT_VERSION) return emptyK01Progress(courseId);
  const validSceneIds = new Set(k01Scenes.map((scene) => scene.id));
  const sceneProgress: Record<string, K01SceneProgress> = {};
  if (source.sceneProgress && typeof source.sceneProgress === "object" && !Array.isArray(source.sceneProgress)) {
    for (const [sceneId, sceneValue] of Object.entries(source.sceneProgress)) {
      if (!validSceneIds.has(sceneId)) continue;
      const cleaned = cleanSceneProgress(sceneValue);
      if ("artifact:transferTrack" in cleaned.values) {
        const transferTrack = courseId === "el-etg" ? "el-etg" : "school";
        cleaned.values["artifact:transferTrack"] = transferTrack;
        const selectedCaseId = cleaned.values["artifact:systemCaseId"];
        if (selectedCaseId && !k01Cases.transferCases[transferTrack].some((item) => item.id === selectedCaseId)) {
          delete cleaned.values["artifact:systemCaseId"];
        }
      }
      sceneProgress[sceneId] = cleaned;
    }
  }
  const artifacts: Record<string, Record<string, string>> = {};
  const validArtifactIds = new Set(k01Artifacts.map((artifact) => artifact.id));
  if (source.artifacts && typeof source.artifacts === "object" && !Array.isArray(source.artifacts)) {
    for (const [artifactId, artifactValue] of Object.entries(source.artifacts).slice(0, 8)) {
      if (!validArtifactIds.has(artifactId)) continue;
      const cleaned = cleanStringRecord(artifactValue, 1600);
      if ("transferTrack" in cleaned) {
        const transferTrack = courseId === "el-etg" ? "el-etg" : "school";
        cleaned.transferTrack = transferTrack;
        if (cleaned.systemCaseId && !k01Cases.transferCases[transferTrack].some((item) => item.id === cleaned.systemCaseId)) {
          delete cleaned.systemCaseId;
        }
      }
      artifacts[artifactId] = cleaned;
    }
  }
  return {
    schemaVersion: 3,
    contentVersion: CONTENT_VERSION,
    courseId,
    currentSceneId: source.currentSceneId && validSceneIds.has(source.currentSceneId) ? source.currentSceneId : k01Scenes[0].id,
    completedSceneIds: [...new Set(cleanStringArray(source.completedSceneIds).filter((id) => validSceneIds.has(id)))],
    listenedSceneIds: [...new Set(cleanStringArray(source.listenedSceneIds).filter((id) => validSceneIds.has(id)))],
    sceneProgress,
    artifacts,
  };
}

function legacyHasStarted(source: Record<string, unknown>, progress: K01Progress): boolean {
  return source.activeView === "scene"
    || progress.completedSceneIds.length > 0
    || progress.listenedSceneIds.length > 0
    || Object.keys(progress.sceneProgress).length > 0
    || Object.keys(progress.artifacts).length > 0;
}

function migrateLegacyProgress(): FramlageProgress {
  const fresh = emptyFramlageProgress();
  try {
    const legacyRaw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacyRaw) {
      const source = JSON.parse(legacyRaw) as Record<string, unknown>;
      const courseId: CourseId = source.educationTrackId === "el-etg" ? "el-etg" : "general";
      const migrated = source.contentVersion === CONTENT_VERSION
        ? cleanCourseProgress(source, courseId)
        : emptyK01Progress(courseId);
      fresh.courses[courseId] = migrated;
      if (legacyHasStarted(source, migrated)) {
        fresh.activeCourseId = courseId;
        fresh.activeView = "course";
        fresh.startedCourseIds = [courseId];
      }
      return fresh;
    }
    const oldestRaw = localStorage.getItem(OLDEST_STORAGE_KEY);
    if (oldestRaw) {
      const oldest = JSON.parse(oldestRaw) as { educationTrackId?: unknown };
      if (oldest.educationTrackId === "el-etg") fresh.activeCourseId = "el-etg";
    }
  } catch {
    // Äldre data får aldrig hindra en ren kursöversikt.
  }
  return fresh;
}

export function loadFramlageProgress(): FramlageProgress {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const migrated = migrateLegacyProgress();
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
        localStorage.removeItem(LEGACY_STORAGE_KEY);
        localStorage.removeItem(OLDEST_STORAGE_KEY);
      } catch {
        // Appen fungerar fortfarande med separata kurser i minnet.
      }
      return migrated;
    }
    const source = JSON.parse(raw) as Partial<FramlageProgress>;
    if (source.schemaVersion !== 3 || source.contentVersion !== CONTENT_VERSION) return emptyFramlageProgress();
    const activeCourseId = source.activeCourseId && availableCourseIds.has(source.activeCourseId) ? source.activeCourseId : null;
    const activeViews = new Set<FramlageProgress["activeView"]>(["library", "course", "scene", "data"]);
    const requestedView = source.activeView && activeViews.has(source.activeView) ? source.activeView : "library";
    const coursesSource: Record<string, unknown> = source.courses && typeof source.courses === "object" && !Array.isArray(source.courses)
      ? source.courses as Record<string, unknown>
      : {};
    return {
      schemaVersion: 3,
      contentVersion: CONTENT_VERSION,
      activeView: activeCourseId ? requestedView : "library",
      activeCourseId,
      startedCourseIds: [...new Set(cleanStringArray(source.startedCourseIds).filter((id): id is CourseId => availableCourseIds.has(id as CourseId)))],
      courses: {
        general: cleanCourseProgress(coursesSource.general, "general"),
        "el-etg": cleanCourseProgress(coursesSource["el-etg"], "el-etg"),
      },
    };
  } catch {
    return emptyFramlageProgress();
  }
}

export function saveFramlageProgress(progress: FramlageProgress): boolean {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
    return true;
  } catch {
    return false;
  }
}

export function resetCourseProgress(progress: FramlageProgress, courseId: CourseId): FramlageProgress {
  return {
    ...progress,
    activeView: "course",
    activeCourseId: courseId,
    startedCourseIds: progress.startedCourseIds.filter((id) => id !== courseId),
    courses: {
      ...progress.courses,
      [courseId]: emptyK01Progress(courseId),
    },
  };
}

export const framlageStorageKey = STORAGE_KEY;
export const framlageLegacyStorageKey = LEGACY_STORAGE_KEY;
